/**
 * What a live host can do with Badger (docs/features/badger-cohost.md, Phase 2 contract): bring him in, send him off,
 * mute and unmute him. Like the stage's actions (`stageActions.ts`) they know nothing of the UI: each checks what the
 * page can check, runs as the live host (the API and the agent decide), and answers what it did or why not. Bringing in
 * and sending off go through the API; Mute is an RPC to Badger himself, which only a live host on his stage may make.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { RpcError, type Participant, type PerformRpcParams, type Room } from 'livekit-client'
import { apiFailure } from '@/lib/apiFailure'
import { watchApi } from './api'
import { NOT_LIVE, refusal, type StageActionResult } from './stageActions'
import type { StageCohost } from './types'
import { useCohostMuted, useCohostParticipant } from './useCohost'

/** The RPC Badger answers to mute or unmute his voice: `{"muted": bool}` in, `{"muted": bool}` back. */
export const COHOST_MUTE_METHOD = 'cohost.mute'

export const DISPATCH_FAILED = 'Badger couldn’t join. Try again.'
export const NOT_HERE = 'Badger isn’t on the stage'
export const NO_ANSWER = 'Badger didn’t answer. Try again.'
export const HOSTS_ONLY = 'Only a live host on this stage can do that'

export type CohostMuteResult = { ok: true; muted: boolean; shown: string } | { ok: false; reason: string }

export interface CohostActions {
  /** Asks Badger onto the stage. He joins a moment later (presence comes from the room). */
  bringIn(): Promise<StageActionResult>
  /** Sends him off: the request is cleared and he leaves. */
  sendOff(): Promise<StageActionResult>
  /** Mutes or unmutes his voice for everyone, at once; answers what he says he is now. */
  setMuted(muted: boolean): Promise<CohostMuteResult>
}

type CohostApi = Pick<typeof watchApi, 'bringCohost' | 'sendOffCohost'>

export interface CohostActionContext {
  stageId: string
  /** This tab's host is live on the stage: only then do actions run. */
  live: boolean
  /** This tab's LiveKit identity, the connection live on the stage that asks for him. */
  identity: string | null
  /** Badger's identity while he is in the room, else null. */
  cohostIdentity: string | null
  /** The room's RPC as this tab (`room.localParticipant.performRpc`). */
  performRpc?(params: PerformRpcParams): Promise<string>
  /** The API, for tests. */
  api?: CohostApi
}

const fail = (reason: string): { ok: false; reason: string } => ({ ok: false, reason })

/** Why the API refused: a dispatch that failed, a connection not live here, else as the stage's actions say it. */
function cohostRefusal(failure: unknown): string {
  const { status } = apiFailure(failure)
  if (status === 503) return DISPATCH_FAILED
  if (status === 422) return NOT_LIVE
  return refusal(failure)
}

/** Why the RPC failed: he has gone, he refused the caller, or he didn't answer in time. */
function rpcRefusal(failure: unknown): string {
  const code = failure instanceof RpcError ? failure.code : (failure as { code?: unknown } | null)?.code
  if (code === RpcError.ErrorCode.RECIPIENT_NOT_FOUND || code === RpcError.ErrorCode.RECIPIENT_DISCONNECTED) return NOT_HERE
  if (code === RpcError.ErrorCode.APPLICATION_ERROR) return HOSTS_ONLY
  return NO_ANSWER
}

/** His answer to `cohost.mute`, `{"muted": bool}`; null for anything else. */
export function mutedFrom(payload: string): boolean | null {
  try {
    const data: unknown = JSON.parse(payload)
    const muted = (data as { muted?: unknown } | null)?.muted
    return typeof muted === 'boolean' ? muted : null
  } catch {
    return null
  }
}

/** Badger's actions over a context read at each call (`context()`), so they stay the same functions as it changes. */
export function createCohostActions(context: () => CohostActionContext): CohostActions {
  const apiOf = (ctx: CohostActionContext) => ctx.api ?? watchApi
  return {
    async bringIn() {
      const ctx = context()
      if (!ctx.live || !ctx.identity) return fail(NOT_LIVE)
      try {
        await apiOf(ctx).bringCohost(ctx.stageId, ctx.identity)
        return { ok: true, shown: 'Badger is joining' }
      } catch (failure) {
        return fail(cohostRefusal(failure))
      }
    },

    async sendOff() {
      const ctx = context()
      if (!ctx.live) return fail(NOT_LIVE)
      try {
        await apiOf(ctx).sendOffCohost(ctx.stageId)
        return { ok: true, shown: 'Badger is leaving' }
      } catch (failure) {
        return fail(cohostRefusal(failure))
      }
    },

    async setMuted(muted) {
      const ctx = context()
      if (!ctx.live) return fail(NOT_LIVE)
      if (!ctx.cohostIdentity || !ctx.performRpc) return fail(NOT_HERE)
      let answer: string
      try {
        answer = await ctx.performRpc({ destinationIdentity: ctx.cohostIdentity, method: COHOST_MUTE_METHOD, payload: JSON.stringify({ muted }) })
      } catch (failure) {
        return fail(rpcRefusal(failure))
      }
      // He did it; an answer the page can't read leaves his track to say (it is the truth either way).
      const now = mutedFrom(answer) ?? muted
      return { ok: true, muted: now, shown: now ? 'Badger is muted' : 'Badger is unmuted' }
    },
  }
}

/**
 * Where Badger is for the console: not asked for (`off`), asked for but not in the room yet (`joining`), in the room
 * (`present`), or still in the room once no longer asked for (`leaving`, after Send off). Asked for and present are
 * independent; the console says each honestly.
 */
export type CohostStatus = 'off' | 'joining' | 'present' | 'leaving'

export function cohostStatus(asked: boolean, present: boolean): CohostStatus {
  if (present) return asked ? 'present' : 'leaving'
  return asked ? 'joining' : 'off'
}

/** Badger in the host's console: where he is, whether he is muted, and the actions. */
export interface CohostConsole {
  status: CohostStatus
  /** His audio track is muted, or he just said so and his track hasn't followed yet. */
  muted: boolean
  actions: CohostActions
}

export interface CohostConsoleInput {
  room: Room
  stageId: string
  live: boolean
  identity: string | null
  /** The stage's request for him (`StageLive.cohost`): the room metadata's, the stage payload's before it. */
  asked: StageCohost | null | undefined
}

/**
 * Badger for the host's console: his presence in the room, his track's mute, and the actions. An answer from the API
 * (asked for, sent off) counts until the room's metadata moves from what it said when asked, and Badger's answer to Mute
 * until his track's state moves, so the console never flickers back while the room catches up. Re-renders when he
 * comes or goes and on his mute, not with his speaking or state. Not live, he reads as not in the room (the console is
 * a live host's only).
 */
export function useCohostConsole({ room, stageId, live, identity, asked }: CohostConsoleInput): CohostConsole {
  // Only a live host's console shows him: everyone else's page leaves the room's arrivals alone here.
  const participant = useCohostParticipant(room, live ? stageId : null)
  const trackMuted = useCohostMuted(participant)
  const roomAsked = Boolean(asked)

  // Answers hold while the room says what it said when asked (derived, never cleared during render), and are dropped
  // once that moves, so a later move back can't revive them.
  const [askAnswer, setAskAnswer] = useState<{ stageId: string; asked: boolean; roomAsked: boolean } | null>(null)
  const askHolds = askAnswer !== null && askAnswer.stageId === stageId && askAnswer.roomAsked === roomAsked
  const [muteAnswer, setMuteAnswer] = useState<{ participant: Participant; muted: boolean; trackMuted: boolean } | null>(null)
  const muteHolds = muteAnswer !== null && muteAnswer.participant === participant && muteAnswer.trackMuted === trackMuted
  useEffect(() => {
    if (askAnswer && !askHolds) setAskAnswer(null)
    if (muteAnswer && !muteHolds) setMuteAnswer(null)
  }, [askAnswer, askHolds, muteAnswer, muteHolds])

  const latest = useRef({ stageId, live, identity, participant, roomAsked, trackMuted, room })
  latest.current = { stageId, live, identity, participant, roomAsked, trackMuted, room }

  const actions = useMemo<CohostActions>(() => {
    const base = createCohostActions(() => {
      const now = latest.current
      return {
        stageId: now.stageId,
        live: now.live,
        identity: now.identity,
        cohostIdentity: now.participant?.identity ?? null,
        performRpc: (params) => now.room.localParticipant.performRpc(params),
      }
    })
    /** Runs `action` and keeps its answer for the stage it was asked on, while that stage is still on screen. */
    const asking = (action: () => Promise<StageActionResult>, asked: boolean) => async () => {
      const { stageId: on, roomAsked: before } = latest.current
      const result = await action()
      if (result.ok && latest.current.stageId === on) setAskAnswer({ stageId: on, asked, roomAsked: before })
      return result
    }
    return {
      bringIn: asking(() => base.bringIn(), true),
      sendOff: asking(() => base.sendOff(), false),
      async setMuted(muted) {
        const { participant: whom, trackMuted: before } = latest.current
        const result = await base.setMuted(muted)
        if (result.ok && whom && latest.current.participant === whom) setMuteAnswer({ participant: whom, muted: result.muted, trackMuted: before })
        return result
      },
    }
  }, [])

  const status = cohostStatus(askHolds ? askAnswer.asked : roomAsked, participant !== null)
  const muted = muteHolds ? muteAnswer.muted : trackMuted
  return useMemo(() => ({ status, muted, actions }), [status, muted, actions])
}
